import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  DEVICE_TYPES,
  RESERVATION_TYPES,
  fromMillidinars,
  toIqd,
  type CreateDeviceInput,
  type DeviceCategoryDto,
  type DeviceDto,
  type DeviceType,
  type ReservationType,
  type UpdateDeviceInput,
} from '@gc/shared';

import { ImageUpload } from '@/features/settings/controls';
import { api, ApiError } from '@/lib/api';

interface AddDeviceDialogProps {
  categories: DeviceCategoryDto[];
  /** When present, the dialog edits this device instead of creating one. */
  device?: DeviceDto | null;
  onClose: () => void;
  /** Fired after a successful create OR edit — the board reloads either way. */
  onCreated: () => void;
}

interface FieldError {
  field: string;
  message: string;
}

/** Whole-IQD string for a price input, from a millidinar wire string. */
function iqdInputValue(md: string): string {
  return String(toIqd(fromMillidinars(md)));
}

/**
 * Add Device (SPEC §5.2) — and, since wave 3 (#5), Edit Device: the same
 * fixed field list prefilled from the tile's Actions menu, submitted as a
 * `PATCH /devices/:id` instead of a create. One dialog, because the spec's
 * field list is identical and two copies would drift.
 *
 * The field list must not be trimmed: Device name, Tag, Device type,
 * Reservation type, Hourly price, Single game price, Double price, Category,
 * Enable free mode.
 */
export function AddDeviceDialog({ categories, device, onClose, onCreated }: AddDeviceDialogProps) {
  const { t } = useTranslation();
  const isEdit = device != null;

  const [name, setName] = useState(device?.name ?? '');
  const [tag, setTag] = useState(device?.tag ?? '');
  const [deviceType, setDeviceType] = useState<DeviceType>(device?.deviceType ?? 'PC');
  const [reservationType, setReservationType] = useState<ReservationType>(
    device?.reservationType ?? 'HOURLY',
  );
  const [hourlyPrice, setHourlyPrice] = useState(
    device ? iqdInputValue(device.hourlyPriceMd) : '0',
  );
  const [singleGamePrice, setSingleGamePrice] = useState(
    device ? iqdInputValue(device.singleGamePriceMd) : '0',
  );
  const [doublePrice, setDoublePrice] = useState(
    device ? iqdInputValue(device.doublePriceMd) : '0',
  );
  const [categoryId, setCategoryId] = useState(device?.categoryId ?? categories[0]?.id ?? '');
  const [freeModeEnabled, setFreeModeEnabled] = useState(device?.freeModeEnabled ?? false);
  const [isHidden, setIsHidden] = useState(device?.isHidden ?? false);
  const [tvIp, setTvIp] = useState(device?.tvIpAddress ?? '');
  const [tvHdmi, setTvHdmi] = useState(device?.tvHdmiInput ? String(device.tvHdmiInput) : '');
  // §7 auto-detect: a device that already has an IP opens on the familiar
  // manual field; a fresh TV setup starts on the scanner.
  const [tvIpMode, setTvIpMode] = useState<'auto' | 'manual'>(
    device?.tvIpAddress ? 'manual' : 'auto',
  );
  const [tvScanning, setTvScanning] = useState(false);
  const [tvScan, setTvScan] = useState<{
    status: 'ok' | 'no_bridge' | 'bridge_outdated' | 'timeout' | 'failed';
    tvs: Array<{
      ip: string;
      adbState: 'authorized' | 'unauthorized' | 'offline' | 'unknown';
      model: string | null;
      inUseBy: string | null;
    }>;
  } | null>(null);
  /** Transient outcome line under the TV Test buttons. */
  const [tvTestNote, setTvTestNote] = useState<string | null>(null);
  const [tvTesting, setTvTesting] = useState(false);

  // The DTO carries a signed URL, not the asset id, so the id is tracked
  // separately and only for what changed HERE: `undefined` means the picture
  // was left alone and must not be sent at all, or editing the price of a
  // device would clear its photo.
  const [imageUrl, setImageUrl] = useState<string | null>(device?.imageUrl ?? null);
  const [imageAssetId, setImageAssetId] = useState<string | null | undefined>(undefined);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const errorFor = (field: string) => errors.find((error) => error.field === field)?.message;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    // Free mode is PC-only (the schema refuses otherwise); switching an
    // edited device's type away from PC must drop the flag rather than send a
    // body the server will bounce whole.
    const effectiveFreeMode = deviceType === 'PC' && freeModeEnabled;

    const payload: CreateDeviceInput & UpdateDeviceInput = {
      name,
      tag: tag.trim() || null,
      deviceType,
      categoryId,
      reservationType,
      hourlyPrice: Number(hourlyPrice) || 0,
      singleGamePrice: Number(singleGamePrice) || 0,
      doublePrice: Number(doublePrice) || 0,
      freeModeEnabled: effectiveFreeMode,
      isHidden,
      // Untouched stays `undefined`, which JSON.stringify drops from the body
      // entirely — so the server sees no key and leaves the column alone.
      imageMediaAssetId: imageAssetId,
      // TV control is a CONSOLE thing (§7): only a PlayStation has an Android
      // TV in front of it. A device whose type just LEFT console drops its TV
      // settings (null), since the form no longer shows them; any other
      // non-console row leaves the columns alone (undefined) — a PC that was
      // given a TV under the old dialog must not lose it because its price
      // was edited.
      tvIpAddress:
        deviceType === 'CONSOLE'
          ? tvIp.trim() || null
          : device?.deviceType === 'CONSOLE'
            ? null
            : undefined,
      tvHdmiInput:
        deviceType === 'CONSOLE'
          ? tvHdmi
            ? Number(tvHdmi)
            : null
          : device?.deviceType === 'CONSOLE'
            ? null
            : undefined,
    };

    try {
      if (isEdit) await api.patch(`/devices/${device.id}`, payload);
      else await api.post('/devices', payload);
      onCreated();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        setErrors(caught.details as FieldError[]);
      } else {
        setErrors([
          {
            field: '(root)',
            message: caught instanceof Error ? caught.message : 'Could not add the device.',
          },
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-lg overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {isEdit ? t('deviceBoard.editDevice', { device: device.name }) : t('devices.addDevice')}
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.deviceName')}
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className={inputClass}
            />
            {errorFor('name') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('name')}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">{t('devices.tag')}</span>
            <input value={tag} onChange={(e) => setTag(e.target.value)} className={inputClass} />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.category')}
            </span>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
              className={inputClass}
            >
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {errorFor('categoryId') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('categoryId')}</p>
            ) : null}
            {/* A category is required, so on a venue that has none this select is
                empty and saving can only fail. Say where to make one instead of
                letting them find that out by pressing Save. */}
            {categories.length === 0 ? (
              <p className="mt-1 text-xs text-content-secondary">
                <Link to="/settings/device-categories" className="text-primary underline">
                  {t('devices.noCategories')}
                </Link>
              </p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.deviceType')}
            </span>
            <select
              value={deviceType}
              onChange={(e) => setDeviceType(e.target.value as DeviceType)}
              className={inputClass}
            >
              {DEVICE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(`devices.type.${type}`)}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.reservationType')}
            </span>
            <select
              value={reservationType}
              onChange={(e) => setReservationType(e.target.value as ReservationType)}
              className={inputClass}
            >
              {RESERVATION_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type === 'HOURLY' ? t('devices.hourly') : t('devices.roundBased')}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.hourlyPrice')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={hourlyPrice}
              onChange={(e) => setHourlyPrice(e.target.value)}
              className={`${inputClass} tabular`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.singleGamePrice')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={singleGamePrice}
              onChange={(e) => setSingleGamePrice(e.target.value)}
              className={`${inputClass} tabular`}
            />
            {errorFor('singleGamePrice') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('singleGamePrice')}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('devices.doublePrice')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={doublePrice}
              onChange={(e) => setDoublePrice(e.target.value)}
              className={`${inputClass} tabular`}
            />
          </label>

          {/*
            PCs ONLY, and absent rather than greyed out on everything else.

            Free mode means handing the customer the Windows desktop instead of
            the portal — so it exists only on a machine that HAS a portal to
            leave. A table, a billiard table and a console have none, and a
            disabled checkbox on their form was still a checkbox: it invited the
            question "why can't I tick this?" on three device types where the
            answer is "there is nothing here it could do".
          */}
          {deviceType === 'PC' ? (
            <label className="col-span-2 flex items-start gap-2 rounded-control border border-subtle bg-input px-3 py-2">
              <input
                type="checkbox"
                checked={freeModeEnabled}
                onChange={(e) => setFreeModeEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[var(--brand-primary)]"
              />
              <span>
                <span className="block text-sm text-content">{t('devices.enableFreeMode')}</span>
                {/* The two directions are not symmetric (owner, 2026-10-14). ON
                  reaches a PC somebody is sitting at straight away — the server
                  hands it the desktop. OFF only changes the device: the sitting
                  running now stays in Free Mode until it ends. Only on a PC that
                  exists; a new one has no sitting to wait for. */}
                {isEdit ? (
                  <span data-free-mode-hint className="block text-xs text-content-muted">
                    {t('devices.freeModeOffHint')}
                  </span>
                ) : null}
              </span>
            </label>
          ) : null}

          {/*
            The PC's game (owner, 2026-09-24) — shown, not edited, here. Its
            video is what this PC's lock screen plays, and it is set on the
            Screens page ("Set game…"), where the videos are, for many PCs at
            once; a second writer in this dialog would be one more place for
            a save to half-apply. Only on a PC that already exists: a game
            belongs to a machine, not to a form.
          */}
          {isEdit && device.deviceType === 'PC' && deviceType === 'PC' ? (
            <div
              data-device-game={device.gameId ?? ''}
              className="col-span-2 rounded-control border border-subtle bg-input px-3 py-2"
            >
              <p className="text-sm">
                <span className="text-content-secondary">{t('deviceBoard.game.label')}</span>{' '}
                <span className="font-medium text-content">
                  {device.gameTitle ?? t('deviceBoard.game.none')}
                </span>
              </p>
              <p className="mt-1 text-xs text-content-muted">
                {device.gameTitle ? t('deviceBoard.game.hint') : t('deviceBoard.game.hintNone')}{' '}
                <Link to="/screens" className="text-primary underline">
                  {t('deviceBoard.game.openScreens')}
                </Link>
              </p>
            </div>
          ) : null}

          {/*
            Hidden, NOT retired. The venue's own back-office machine belongs on
            nobody's board, but it still runs the agent and still prints — so
            the label has to say that plainly, or this reads as "switch off"
            and nobody dares tick it.
          */}
          <label className="col-span-2 flex items-start gap-2 rounded-control border border-subtle bg-input px-3 py-2">
            <input
              type="checkbox"
              checked={isHidden}
              onChange={(e) => setIsHidden(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--brand-primary)]"
            />
            <span>
              <span className="block text-sm text-content">{t('devices.hideFromBoard')}</span>
              <span className="block text-xs text-content-muted">
                {t('devices.hideFromBoardHint')}
              </span>
            </span>
          </label>

          {/*
            §7 TV control — the Android TV in front of a console. The video it
            loops comes from the Screens area (the same idle-video assignment
            PCs use); this section only says WHICH TV and WHICH input.

            CONSOLES ONLY, absent rather than greyed out on everything else —
            the same reasoning as the free-mode checkbox above: a PC, a table
            or an arcade cabinet has no TV for this to drive.
          */}
          {deviceType === 'CONSOLE' ? (
            <div className="col-span-2 rounded-control border border-subtle bg-input px-3 py-2">
              <p className="mb-2 text-sm font-medium text-content">{t('devices.tvSection')}</p>
              {/* §7 auto-detect: the bridge sweeps the venue LAN so nobody has
                to read an IP off a television menu; manual entry stays one
                tap away for the odd network. */}
              <div className="mb-2 flex gap-1" role="radiogroup" aria-label={t('devices.tvIp')}>
                {(['auto', 'manual'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    role="radio"
                    aria-checked={tvIpMode === mode}
                    onClick={() => {
                      setTvIpMode(mode);
                      // Results and alerts go stale the moment attention leaves
                      // them — coming back to Auto starts clean rather than
                      // re-showing an old error or an outdated freeness list.
                      setTvScan(null);
                    }}
                    className={`rounded-full px-3 py-1 text-xs ${
                      tvIpMode === mode
                        ? 'bg-primary text-primary-fg'
                        : 'border border-subtle text-content-secondary hover:bg-hover'
                    }`}
                  >
                    {mode === 'auto' ? t('devices.tvIpAutoDetect') : t('devices.tvIpManual')}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                {tvIpMode === 'manual' ? (
                  <label className="block">
                    <span className="mb-1 block text-sm text-content-secondary">
                      {t('devices.tvIp')}
                    </span>
                    <input
                      value={tvIp}
                      onChange={(e) => setTvIp(e.target.value)}
                      placeholder="192.168.1.50"
                      dir="ltr"
                      className={`${inputClass} tabular`}
                    />
                    {errorFor('tvIpAddress') ? (
                      <p className="mt-1 text-xs text-danger">{errorFor('tvIpAddress')}</p>
                    ) : null}
                  </label>
                ) : (
                  <div className="block">
                    <span className="mb-1 block text-sm text-content-secondary">
                      {t('devices.tvIp')}
                    </span>
                    <button
                      type="button"
                      disabled={tvScanning}
                      onClick={() => {
                        setTvScanning(true);
                        setTvScan(null);
                        api
                          .post<{
                            status: 'ok' | 'no_bridge' | 'timeout';
                            tvs: NonNullable<typeof tvScan>['tvs'];
                          }>('/devices/tv-scan')
                          .then((response) => setTvScan(response))
                          .catch(() => setTvScan({ status: 'failed', tvs: [] }))
                          .finally(() => setTvScanning(false));
                      }}
                      className="rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover disabled:opacity-50"
                    >
                      {tvScanning ? t('devices.tvScanning') : t('devices.tvScanButton')}
                    </button>
                    {tvIp.trim() ? (
                      <p dir="ltr" className="tabular mt-1 text-xs text-content-secondary">
                        {tvIp}
                      </p>
                    ) : null}
                    {errorFor('tvIpAddress') ? (
                      <p className="mt-1 text-xs text-danger">{errorFor('tvIpAddress')}</p>
                    ) : null}
                  </div>
                )}
                <label className="block">
                  <span className="mb-1 block text-sm text-content-secondary">
                    {t('devices.tvHdmi')}
                  </span>
                  <select
                    value={tvHdmi}
                    onChange={(e) => setTvHdmi(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">{t('devices.tvHdmiNone')}</option>
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={String(n)}>
                        {t('devices.tvHdmiN', { n })}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {tvIpMode === 'auto' && tvScan ? (
                tvScan.status !== 'ok' ? (
                  <p role="alert" className="mt-2 text-xs text-danger">
                    {tvScan.status === 'no_bridge'
                      ? t('devices.tvScanNoBridge')
                      : tvScan.status === 'bridge_outdated'
                        ? t('devices.tvScanOutdated')
                        : tvScan.status === 'timeout'
                          ? t('devices.tvScanTimeout')
                          : t('devices.tvScanFailed')}
                  </p>
                ) : tvScan.tvs.length === 0 ? (
                  <p className="mt-2 text-xs text-content-muted">{t('devices.tvScanEmpty')}</p>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {tvScan.tvs.map((candidate) => {
                      // A TV another seat already drives is picked only by
                      // mistake; show it greyed with its owner's name.
                      const taken =
                        candidate.inUseBy !== null && candidate.ip !== (device?.tvIpAddress ?? '');
                      return (
                        <li key={candidate.ip}>
                          <button
                            type="button"
                            disabled={taken}
                            data-tv-candidate={candidate.ip}
                            onClick={() => {
                              setTvIp(candidate.ip);
                            }}
                            className={`flex w-full items-center gap-2 rounded-control border px-3 py-2 text-start text-sm ${
                              !taken && tvIp === candidate.ip ? 'border-primary' : 'border-subtle'
                            } ${taken ? 'opacity-50' : 'hover:bg-hover'}`}
                          >
                            <span className="min-w-0 flex-1 truncate text-content">
                              {candidate.model ?? t('devices.tvScanUnnamed')}
                            </span>
                            <span dir="ltr" className="tabular shrink-0 text-xs text-content-muted">
                              {candidate.ip}
                            </span>
                            <span className="shrink-0 text-xs text-content-secondary">
                              {taken
                                ? t('devices.tvScanInUse', { device: candidate.inUseBy })
                                : candidate.adbState === 'authorized'
                                  ? t('devices.tvScanReady')
                                  : candidate.adbState === 'unauthorized'
                                    ? t('devices.tvScanNeedsApproval')
                                    : t('devices.tvScanFound')}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )
              ) : null}
              <p className="mt-2 text-xs text-content-muted">{t('devices.tvIpHint')}</p>
              {tvIp.trim() ? (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {(['idle', 'console'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      disabled={tvTesting}
                      onClick={() => {
                        // A test drives the SAVED row — an unsaved or edited IP
                        // has no device for the bridge to act on yet, and
                        // silently testing the OLD address would tell the owner
                        // a mistyped IP "works".
                        const savedIp = device?.tvIpAddress ?? '';
                        const savedHdmi = device?.tvHdmiInput ?? null;
                        const dirty =
                          tvIp.trim() !== savedIp || (tvHdmi ? Number(tvHdmi) : null) !== savedHdmi;
                        if (!isEdit || dirty) {
                          setTvTestNote(t('devices.tvTestSaveFirst'));
                          return;
                        }
                        setTvTesting(true);
                        setTvTestNote(null);
                        api
                          .post(`/devices/${device.id}/tv-test`, { mode })
                          .then(() => setTvTestNote(t('devices.tvTestSent')))
                          .catch((caught: unknown) =>
                            setTvTestNote(
                              caught instanceof Error ? caught.message : t('devices.tvTestSent'),
                            ),
                          )
                          .finally(() => setTvTesting(false));
                      }}
                      className="rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover disabled:opacity-50"
                    >
                      {mode === 'idle' ? t('devices.tvTestIdle') : t('devices.tvTestConsole')}
                    </button>
                  ))}
                  {tvTestNote ? (
                    <span className="text-xs text-content-muted">{tvTestNote}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="col-span-2">
            <ImageUpload
              label={t('devices.picture')}
              kind="DEVICE_IMAGE"
              currentUrl={imageUrl}
              onUploaded={(assetId, url) => {
                setImageAssetId(assetId);
                setImageUrl(url);
              }}
              onCleared={() => {
                setImageAssetId(null);
                setImageUrl(null);
              }}
            />
            <p className="mt-1 text-xs text-content-muted">{t('devices.pictureHint')}</p>
            {errorFor('imageMediaAssetId') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('imageMediaAssetId')}</p>
            ) : null}
          </div>
        </div>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        {/* Field errors are rendered beside their own field, which in a form
            this tall can sit well above the fold — press Save and the dialog
            looks like it did nothing (review finding). Repeat them here, at
            the button the person is actually looking at. */}
        {errors.length > 0 && !errorFor('(root)') ? (
          <ul role="alert" className="mt-3 space-y-0.5 text-sm text-danger">
            {errors.map((error) => (
              <li key={error.field}>{error.message}</li>
            ))}
          </ul>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>
  );
}
